import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import {
  Users, ClipboardCheck, CreditCard, Wallet, Bell, CalendarDays,
  FileCheck, Target, GraduationCap, Briefcase, BarChart2, CalendarClock,
  Award, Globe, UserCheck, ChevronRight,
} from 'lucide-react';

const SECTIONS = [
  {
    title: 'Employee Management',
    accent: 'bg-success',
    items: [
      { path: '/employees', icon: UserCheck, label: 'Employees', desc: 'Manage employee records & profiles', color: 'text-success', bg: 'bg-success/10', ring: 'ring-emerald-100' },
      { path: '/employee-documents', icon: FileCheck, label: 'Documents', desc: 'Employee docs, contracts & IDs', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/recruitment', icon: Briefcase, label: 'Recruitment', desc: 'Job postings, applications & interviews', color: 'text-violet-600', bg: 'bg-violet-50', ring: 'ring-violet-100' },
    ],
  },
  {
    title: 'Attendance & Leave',
    accent: 'bg-info',
    items: [
      { path: '/attendance-dashboard', icon: ClipboardCheck, label: 'Attendance', desc: 'Daily attendance tracking', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/leave-approvals', icon: Bell, label: 'Leave Approvals', desc: 'Review & approve leave requests', color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
      { path: '/leave-calendar', icon: CalendarDays, label: 'Leave Calendar', desc: 'Visual leave calendar', color: 'text-teal-600', bg: 'bg-teal-50', ring: 'ring-teal-100' },
      { path: '/schedule', icon: CalendarClock, label: 'Schedules', desc: 'Shift scheduling & rosters', color: 'text-muted-foreground', bg: 'bg-muted/50', ring: 'ring-border' },
    ],
  },
  {
    title: 'Payroll & Compensation',
    accent: 'bg-teal-500',
    items: [
      { path: '/payroll', icon: CreditCard, label: 'Payroll', desc: 'Salary processing & WPS', color: 'text-success', bg: 'bg-success/10', ring: 'ring-emerald-100' },
      { path: '/loans', icon: Wallet, label: 'Loans', desc: 'Employee loans & installments', color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
    ],
  },
  {
    title: 'Performance & Training',
    accent: 'bg-violet-500',
    items: [
      { path: '/performance-reviews', icon: Target, label: 'Performance Reviews', desc: 'Employee reviews & goals', color: 'text-violet-600', bg: 'bg-violet-50', ring: 'ring-violet-100' },
      { path: '/training', icon: GraduationCap, label: 'Training', desc: 'Training programs & certifications', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/staff-performance', icon: Award, label: 'Staff Performance', desc: 'Attendance-based performance metrics', color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
    ],
  },
  {
    title: 'Analytics & Compliance',
    accent: 'bg-indigo-500',
    items: [
      { path: '/hr-analytics', icon: BarChart2, label: 'HR Analytics', desc: 'Workforce analytics & trends', color: 'text-indigo-600', bg: 'bg-indigo-50', ring: 'ring-indigo-100' },
      { path: '/country-laws', icon: Globe, label: 'Country Labor Laws', desc: 'Multi-country labor compliance', color: 'text-success', bg: 'bg-success/10', ring: 'ring-emerald-100' },
    ],
  },
];

function FeatureCard({ item, navigate }) {
  return (
    <button
      onClick={() => navigate(item.path)}
      className="group flex items-center gap-3.5 p-4 rounded-xl bg-white border border-border hover:border-border hover:shadow-md transition-all duration-200 text-left"
      data-testid={`nav-${item.path.replace('/', '')}`}
    >
      <div className={`w-10 h-10 rounded-xl ${item.bg} ${item.color} flex items-center justify-center shrink-0 ring-1 ${item.ring} group-hover:scale-110 transition-transform duration-200`}>
        <item.icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-foreground text-sm group-hover:text-success transition-colors">{item.label}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{item.desc}</p>
      </div>
      <ChevronRight size={14} className="text-stone-200 group-hover:text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-all" />
    </button>
  );
}

export default function HRModulePage() {
  const navigate = useNavigate();
  return (
    <DashboardLayout module="hr">
      <div className="space-y-10 max-w-6xl mx-auto" data-testid="hr-module-page">
        {/* Header */}
        <div className="relative">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-700 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Users size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">HR & Workforce</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Employee lifecycle, payroll, attendance & compliance</p>
            </div>
          </div>
        </div>

        {/* Sections */}
        {SECTIONS.map((section, si) => (
          <div key={section.title} style={{ animationDelay: `${si * 60}ms` }}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-1 h-5 rounded-full ${section.accent}`} />
              <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{section.title}</h2>
              <div className="flex-1 h-px bg-muted" />
              <span className="text-[10px] text-muted-foreground font-medium">{section.items.length} items</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {section.items.map((item) => (
                <FeatureCard key={item.path} item={item} navigate={navigate} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}
